import { apiService } from "../api.service";

// Domain Models
import {
  AnalyticsOverview,
  AnalyticsOperations,
  AnalyticsRatings
} from "../../models/app/domain";

const analyticsParams = ({ branchId, dateFrom, dateTo, compare } = {}) => {
  const params = new URLSearchParams();
  if (branchId) params.set("branch_id", branchId);
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  if (compare === true) params.set("compare", "true");
  return params;
};

const buildExportQuery = (branchId, dateFrom, dateTo, compare) => {
  const qs = analyticsParams({ branchId, dateFrom, dateTo, compare }).toString();
  return qs ? `?${qs}` : "";
};

/**
 * Exports the V2 Analytics dashboard as a PDF. The back-end renders the
 * same sections shown on the dashboard (overview KPIs + sparklines,
 * operations charts, ratings, technician ranking, low-rating reviews)
 * with the applied filters (branch, period, compare period) carried
 * into the report header.
 *
 * @param branchId - Branch id (or empty / "" for "all branches").
 * @param dateFrom - Period start (YYYY-MM-DD).
 * @param dateTo - Period end (YYYY-MM-DD).
 * @param compare - When true, include previous-period comparison deltas.
 * @return - ServiceResponse || ErrorResponse
 */
export const exportAnalythicsPDF = async (
  branchId,
  dateFrom,
  dateTo,
  compare
) => {
  const response = await apiService.getBlob(
    `/businesses/analytics/export${buildExportQuery(
      branchId,
      dateFrom,
      dateTo,
      compare
    )}`
  );
  return response;
};

/**
 * Adds a `_t=<timestamp>` cache-buster on every call. The dashboard is
 * read-after-write live data: when the user finishes a service (or any
 * status changes) and refreshes, we MUST hit the BE and not get a stale
 * 304 with an empty body cached from before the change. Express's
 * default ETag would otherwise serve the stale snapshot.
 */
const buildDashboardQuery = filters => {
  const params = analyticsParams(filters);
  params.set("_t", Date.now());
  return `?${params.toString()}`;
};

/**
 * Fetches the Analytics dashboard "Overview" payload (KPI cards + sparklines).
 *
 * @param {{ branchId?: number, dateFrom?: string, dateTo?: string, compare?: boolean }} filters
 * @returns {Promise<AnalyticsOverviewResponse>|Promise<ErrorResponse>}
 */
export const fetchAnalyticsOverview = async (filters = {}) => {
  const response = await apiService.get(
    `/businesses/analytics/overview${buildDashboardQuery(filters)}`
  );
  if (response.hasError) return response;
  return { overview: new AnalyticsOverview(response.data || {}) };
};

/**
 * Fetches the Analytics dashboard "Operations" payload (response time
 * trend, duration by service type, decline reasons).
 *
 * @param {{ branchId?: number, dateFrom?: string, dateTo?: string }} filters
 * @returns {Promise<AnalyticsOperationsResponse>|Promise<ErrorResponse>}
 */
export const fetchAnalyticsOperations = async (filters = {}) => {
  const response = await apiService.get(
    `/businesses/analytics/operations${buildDashboardQuery(filters)}`
  );
  if (response.hasError) return response;
  return { operations: new AnalyticsOperations(response.data || {}) };
};

/**
 * Fetches the Analytics dashboard "Ratings deep-dive" payload
 * (distribution, trend, technician ranking, recent low ratings).
 *
 * @param {{ branchId?: number, dateFrom?: string, dateTo?: string }} filters
 * @returns {Promise<AnalyticsRatingsResponse>|Promise<ErrorResponse>}
 */
export const fetchAnalyticsRatings = async (filters = {}) => {
  const response = await apiService.get(
    `/businesses/analytics/ratings${buildDashboardQuery(filters)}`
  );
  if (response.hasError) return response;
  return { ratings: new AnalyticsRatings(response.data || {}) };
};
